import { createContext, useContext, useCallback, useEffect, useState } from 'react';
import { LIST_TOOLBAR_MODES } from 'domains/reporter/RichTextEditor/plugins/list/constants';
import type {
  ListToolbarModes,
  ListVariant,
} from 'domains/reporter/RichTextEditor/plugins/list/constants';
import { getCurrentActiveListVariant } from 'domains/reporter/RichTextEditor/plugins/list/utils';
import {
  demoteListItems as _demoteListItems,
  increaseIndent as _increaseIndent,
  decreaseIndent as _decreaseIndent,
  switchListVariant as _switchListVariant,
  convertToList as _convertToList,
} from 'domains/reporter/RichTextEditor/plugins/list/functions';
import { Range, useSlateStatic, useSlateSelection } from 'domains/reporter/RichTextEditor/core';

export type CurrentActiveListVariantState = ListVariant | null;

export type CurrentListStoreContextBag = {
  listToolbarModes: ListToolbarModes;
  currentActiveListVariant: CurrentActiveListVariantState;
  setCurrentActiveListVariant: (arg1: CurrentActiveListVariantState) => void;
};

export type CurrentListContextBag = {
  listToolbarModes: ListToolbarModes;
  currentActiveListVariant: CurrentActiveListVariantState;
  setCurrentActiveListVariant: (arg1: CurrentActiveListVariantState) => void;
  switchListVariant: (range: Range) => void;
  insertList: (arg1: { variant: ListVariant; range: Range }) => void;
  demoteListItems: (range: Range) => void;
  increaseIndent: () => void;
  decreaseIndent: () => void;
};

const CurrentListStoreContext = createContext<Nullable<CurrentListStoreContextBag>>(undefined);
const CurrentListContext = createContext<Nullable<CurrentListContextBag>>(undefined);

const useCurrentListStoreInternal = (): CurrentListStoreContextBag => {
  const [currentActiveListVariant, setCurrentActiveListVariant] =
    useState<CurrentActiveListVariantState>(null);

  const listToolbarModes = LIST_TOOLBAR_MODES;

  return {
    listToolbarModes,
    currentActiveListVariant,
    setCurrentActiveListVariant,
  };
};

const useCurrentListEditorInternal = (): CurrentListContextBag => {
  const { listToolbarModes, currentActiveListVariant, setCurrentActiveListVariant } =
    useCurrentListStore();
  const editor = useSlateStatic();
  const selection = useSlateSelection();

  const insertList = useCallback(
    ({ variant, range }: { variant: ListVariant; range: Range }) => {
      _convertToList(editor, variant, range);
    },
    [editor]
  );

  const demoteListItems = useCallback(
    (range: Range) => {
      if (editor != null) {
        _demoteListItems(editor, range);
      }
    },
    [editor]
  );

  const increaseIndent = useCallback(() => {
    _increaseIndent(editor, currentActiveListVariant);
  }, [currentActiveListVariant, editor]);

  const decreaseIndent = useCallback(() => {
    _decreaseIndent(editor, currentActiveListVariant);
  }, [currentActiveListVariant, editor]);

  const switchListVariant = useCallback(
    (range: Range) => {
      if (editor != null) {
        _switchListVariant(editor, range);
        if (selection !== null && Range.equals(range, selection)) {
          setCurrentActiveListVariant(getCurrentActiveListVariant(editor));
        }
      }
    },
    [editor, selection, setCurrentActiveListVariant]
  );

  useEffect(() => {
    setCurrentActiveListVariant(getCurrentActiveListVariant(editor));
  }, [editor, selection, setCurrentActiveListVariant]);

  return {
    listToolbarModes,
    currentActiveListVariant,
    setCurrentActiveListVariant,
    insertList,
    demoteListItems,
    increaseIndent,
    decreaseIndent,
    switchListVariant,
  };
};

export const useCurrentListStore = (): CurrentListStoreContextBag => {
  const currentListBag = useContext(CurrentListStoreContext);

  if (!currentListBag) {
    throw new Error('useCurrentListStore must be used within provider');
  }

  return currentListBag;
};

export const useCurrentList = (): CurrentListContextBag => {
  const currentListBag = useContext(CurrentListContext);

  if (!currentListBag) {
    throw new Error('useCurrentList must be used within provider');
  }

  return currentListBag;
};

export type ProviderComponentProps = Readonly<{
  children: React.ReactNode;
}>;

export const ListStoreProvider = ({ children }: ProviderComponentProps): React.ReactElement => {
  const currentListBag = useCurrentListStoreInternal();

  return (
    <CurrentListStoreContext.Provider value={currentListBag}>
      {children}
    </CurrentListStoreContext.Provider>
  );
};

export const ListEditorProvider = ({ children }: ProviderComponentProps): React.ReactElement => {
  const currentListBag = useCurrentListEditorInternal();

  return (
    <CurrentListContext.Provider value={currentListBag}>{children}</CurrentListContext.Provider>
  );
};

export const ListProvider = ({ children }: ProviderComponentProps): React.ReactElement => (
  <ListStoreProvider>
    <ListEditorProvider>{children}</ListEditorProvider>
  </ListStoreProvider>
);
